import type { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import type { RiskItem } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const riskPanelMeta = {
  description: 'Highest-severity open defects and non-conformities impacting vessel operations across the fleet.',
  endpoint: 'GET /qhse/defects/search?status=SUBMIT,SAVE&companyId={cgaId}',
  logic: 'Filters open QHSE defects (status SUBMIT or SAVE), derives severity from status + days since observed, sorts by severity descending and limits to top risks.',
}

type Props = {
  risks: RiskItem[]
  onViewAll?: () => void
  onRiskClick?: (risk: RiskItem) => void
}

const severityBadge: Record<RiskItem['severity'], string> = {
  CRITICAL: 'bg-red-50 text-red-700',
  HIGH: 'bg-red-50 text-red-700',
  MEDIUM: 'bg-amber-50 text-amber-700',
  LOW: 'bg-emerald-50 text-emerald-700',
}

const RiskPanel: FC<Props> = ({ risks, onViewAll, onRiskClick }) => {
  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Top Operational Risks</h3>
          <CardInfoButton meta={riskPanelMeta} />
        </div>
        <button
          className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'
          onClick={onViewAll}
        >
          View all <ChevronRight className='w-3 h-3' />
        </button>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full text-left'>
          <thead>
            <tr className='text-[10px] text-slate-400 uppercase tracking-wider'>
              <th className='pb-2 font-medium'>Risk</th>
              <th className='pb-2 font-medium'>Why it Matters</th>
              <th className='pb-2 font-medium'>Impact</th>
              <th className='pb-2 font-medium'>Vessel</th>
            </tr>
          </thead>
          <tbody className='text-[11px]'>
            {risks.length > 0 ? risks.map((risk) => (
              <tr
                key={risk.id}
                className='border-t border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors'
                onClick={() => onRiskClick?.(risk)}
              >
                <td className='py-2.5'>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${severityBadge[risk.severity]}`}>
                    {risk.severity}
                  </span>
                </td>
                <td className='py-2.5 text-slate-700'>{risk.description}</td>
                <td className='py-2.5 text-slate-500'>{risk.impact}</td>
                <td className='py-2.5 font-medium text-slate-800'>{risk.vessel}</td>
              </tr>
            )) : (
              <tr><td colSpan={4} className='py-10 text-center text-slate-400 text-xs'>No active risks</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default RiskPanel
